import '@awesome.me/webawesome/dist/components/button/button.js';
import '@awesome.me/webawesome/dist/styles/webawesome.css';
import '../components/breadcrumb.js';
import '../components/empty_state.js';

import { faBuilding } from '@fortawesome/free-solid-svg-icons';
import { html, LitElement, type TemplateResult } from 'lit';
import { customElement } from 'lit/decorators.js';

import {
    type IOrganizationListItem,
    listOrganizations,
} from '../libraries/api_client/organization.js';
import { filterInactive } from '../libraries/display_settings.js';
import { loadFailed } from '../libraries/error_copy.js';
import { legalFormLabel } from '../libraries/legal_form_options.js';
import { type IListFillActions, listEmptyCard, listFillButtons } from '../libraries/list_actions.js';
import {
    type IListResult,
    type IListState,
    ListController,
    listHostStyles,
    listResultFrom,
} from '../libraries/list_controller.js';
import { listEmptyBody, showsEmptyCard } from '../libraries/list_empty_body.js';
import type { IListColumn } from '../libraries/list_table.js';
import { redirectTo } from '../libraries/navigation.js';
import { entityStatusBadge } from '../libraries/status_badge.js';

const organizationColumns: Array<IListColumn<IOrganizationListItem>> = [
    {
        label: 'Name',
        track: 'm',
        cell: organization => organization.name,
    },
    {
        label: 'Rechtsform',
        cell: organization => legalFormLabel(organization.legalForm),
    },
    {
        label: 'Ort',
        track: 'xs',
        cell: organization => organization.city,
    },
    {
        label: 'Status',
        track: 'xs',
        cell: organization => entityStatusBadge(organization.active),
    },
];

/** The one way to fill the list, which every caller may take. */
const fill: IListFillActions = {
    create: {
        label: 'Organisation anlegen',
        onClick: (): void => redirectTo('/organizations/new'),
    },
};

@customElement('fm-organization-list')
export class FmOrganizationList extends LitElement {

    static styles = listHostStyles;

    private readonly list = new ListController<IOrganizationListItem>(this, {
        label: 'Organisationen',
        columns: (): Array<IListColumn<IOrganizationListItem>> => organizationColumns,
        rowKey: (organization): string => organization.id,
        rowHref: (organization): string => `/organizations/${organization.id}`,
        withChevron: true,
        rowClass: (organization): string => (organization.active ? '' : 'row-inactive'),
        filterRows: filterInactive,
        errorDescription: loadFailed('Die Organisationen', true),
        load: async ({ signal }): Promise<IListResult<IOrganizationListItem>> => listResultFrom(
            await listOrganizations({
                signal,
            }),
            organizations => ({
                rows: organizations,
            })
        ),
        empty: (listState): TemplateResult => this.renderEmptyBody(listState),
        actions: (listState): Array<TemplateResult> => (showsEmptyCard(listState) ? [] : listFillButtons('actions', fill)),
    });

    private renderEmptyBody (listState: IListState<IOrganizationListItem>): TemplateResult {
        return listEmptyBody(listState, {
            hidden: 'Organisationen',
            empty: (): TemplateResult => listEmptyCard(faBuilding, {
                heading: 'Noch keine Organisation angelegt',
                description: 'Legen Sie eine Organisation an, um Ihre Betriebe zu verwalten.',
            }, fill),
        });
    }

    render (): TemplateResult {
        return this.list.render(html`
            <fm-breadcrumb slot="breadcrumb" .items=${[
                {
                    label: 'Alle',
                },
            ]}></fm-breadcrumb>
        `);
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'fm-organization-list': FmOrganizationList;
    }
}
